import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import {
  fetchImportStatus,
  importAbsenceData,
} from "src/Pages/Company/Absences/api";
import {
  ExportImportStatus,
  IImportData,
  IImportErrors,
  ImportType,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

interface ImportAbsenceDataParams {
  companyId: string;
  importType: ImportType;
  file: File;
}

interface ImportResponse {
  importId: string;
}

export const useImportAbsenceData = () => {
  const queryClient = useQueryClient();
  const [importStatus, setImportStatus] = useState<ExportImportStatus | null>(
    null
  );
  const [errors, setErrors] = useState<IImportErrors | null>(null);

  const importMutation = useMutation<
    ImportResponse,
    AxiosError,
    ImportAbsenceDataParams
  >({
    mutationFn: async ({ companyId, importType, file }) =>
      importAbsenceData(companyId, importType, file),
    onSuccess: (data) => {
      queryClient.setQueryData(["importStatus", data.importId], data);
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas importu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const importStatusQuery = useQuery({
    queryKey: ["importStatus", importMutation.data?.importId],
    queryFn: async (): Promise<IImportData> => {
      setImportStatus(null);
      setErrors(null);
      const importData: IImportData = await fetchImportStatus(
        importMutation.data!.importId
      );

      if (importData.status === ExportImportStatus.SUCCEEDED) {
        dynamicNotification(
          __("Importu danych został zakończony pomyślnie."),
          "success"
        );
      } else if (importData.status === ExportImportStatus.FAILED) {
        dynamicNotification(
          __("Importu danych zakończony niepowodzeniem."),
          "error",
          "top-right",
          7000
        );

        setErrors({
          errors: importData.errors,
          rowErrors: importData.rowErrors,
        });
      }

      setImportStatus(importData.status);

      return importData;
    },
    enabled: !!importMutation.data?.importId,
    refetchInterval: (query) =>
      query.state.data?.status === ExportImportStatus.CREATED ? 500 : false,
  });

  return {
    importMutation,
    isStatusFetched: importStatusQuery.isFetched,
    importStatus,
    importErrors: errors,
  };
};
